import http from 'common-module/src/util/http';
import { computed, onUnmounted, ref, shallowReadonly } from '@vue/composition-api';
import { CreateMessage, Message, MessageId, RecommendData, RecordMessage, RobotMessage, SprintRecommendData, UserMessage, actionType } from '../dataType';
import { Subject } from 'rxjs';
import { recordToMessage, replaceRecommendLabel } from '../util/message';
import { useLocale } from './useLocale';
import { useConfig, botAvatar } from './useConfig';
import { v4 as uuidV4 } from 'uuid';
import { SendMessageTypeName, SendMessageType, SendMessageReturnType } from '../types';
import { useInstance } from 'common-module/src/util/useInstance';
import { parabolaAnimation, textDebounceAnimation } from '../util/animation';
import { useIntentTypes } from './useIntentTypes';

const subject = new Subject<Message<any> | Message<any>[]>();
const messages = ref<Message<any>[]>([]);
const finished = ref<boolean>(false);
const DEFAULT_PAGE_SIZE = 10;
const currentPage = ref<number>(0);
const loading = ref<boolean>(false);

export const genMessageId = () => uuidV4();

export const isWaitResponseMessage = (message: Message<any>) => message.type === 'ai.waiting' && message.status === 'receiving';
export const isFailResponseMessage = (message: Message<any>) => message.type === 'ai.waiting' && message.status === 'fail';
export interface Info {
  intent?: string;
  event: Event;
  parabolaAnimation: boolean;
}
export const useMessage = () => {
  const instance = useInstance();
  const { t } = useLocale();
  const { getUserInfo, sendMessage, intent: oldIntent } = useConfig();
  const lastWaitResponseMessage = computed(() => messages.value.find(isWaitResponseMessage));
  const isWaitResponse = computed(() => lastWaitResponseMessage.value !== undefined);
  const lastResponseMessage = computed(() => messages.value[messages.value.length - 1]);
  const findIndex = (id: MessageId) => messages.value.findIndex(message => message.id === id);
  const findLastIndex = (id: MessageId) => messages.value.findLastIndex(message => message.id === id);

  /**
   * 加载消息
   * @param next 是否加载下一页
   * @returns 返回的是当前加载的数据，不含所有数据
   */
  const loadMessage = async (next: boolean = false): Promise<Message<any>[]> => {
    if ((next && finished.value) || loading.value) {
      return messages.value;
    }
    loading.value = true;
    try {
      if (next) {
        currentPage.value++;
      } else {
        currentPage.value = 1;
        messages.value = [];
      }
      const result = await http.post('/chat/record', { pageNum: currentPage.value, pageSize: DEFAULT_PAGE_SIZE });
      finished.value = !result.hasNextPage;
      const data = result.list.reverse().map((item: RecordMessage<any>) => recordToMessage(item, getUserInfo()));
      messages.value = data.concat(messages.value);
      return data;
    } finally {
      loading.value = false;
    }
  };
  /**
   * 加载当前消息的后面的消息，并加入到消息列表中
   * @param prevMessageId 当前消息id（真实后端的消息id）
   */
  const loadNewMessage = async (prevMessageId: number) => {
    const result = await http.post('/chat/latest/news', { id: prevMessageId });
    result.chatRecords.forEach((item: RecordMessage<any>) => {
      const message = recordToMessage(item, getUserInfo());
      message.isRecord = false;
      findMessageForAddOrUpdate(message);
    });
  };
  const addMessage = <T>(message: CreateMessage<T>): Message<T> => {
    const newMessage = {
      ...message,
      status: message.status || 'success'
    };
    messages.value.push(newMessage);
    subject.next(newMessage);
    return newMessage;
  };
  const addOrUpdateMessage = <T>(message: CreateMessage<T>, isLast?: boolean): Message<T> => {
    const index = isLast ? findLastIndex(message.id) : findIndex(message.id);
    if (index >= 0) {
      return updateMessage(message.id, {
        ...message,
        status: message.status || 'success'
      }, false, index);
    }
    return addMessage(message);
  };
  /**
   * 添加用户消息到消息列表
   * @param message 消息
   */
  const addUserMessage = <T>(message: UserMessage<T>): Message<T> => {
    const { userId, avatar } = getUserInfo();
    const newMessage = {
      ...message,
      userId,
      avatar
    };
    return addMessage(newMessage);
  };
  const addUserMessageWithPush = <T>(message: UserMessage<T>): Message<T> => {
    const { userId, avatar } = getUserInfo();
    const newMessage = {
      ...message,
      userId,
      avatar
    };
    return addOrUpdateMessage(newMessage);
  };
  /**
   * 添加用户文本消息到消息列表
   * @param text 文本信息
   */
  const addUserTextMessage = (text: string): Message<string> => {
    return addUserMessage<string>({
      id: genMessageId(),
      content: text,
      type: 'ai.sendText'
    });
  };
  /**
   * 添加机器人消息到消息列表
   * @param message 机器人返回的消息
   */
  const addRobotMessage = <T>(message: RobotMessage<T>): Message<T> => {
    const newMessage = {
      ...message,
      userId: 0,
      avatar: botAvatar
    };
    return addMessage(newMessage);
  };
  /**
   * 添加机器人等待消息到消息列表
   */
  const addRobotWaitMessage = (): Message<string> | null => {
    if (isWaitResponse.value) {
      return null;
    }
    const message: RobotMessage<string> = {
      id: genMessageId(),
      content: t('library.RobotChat.think'),
      type: 'ai.waiting',
      status: 'receiving',
      title: t('library.RobotChat.think')
    };
    return addRobotMessage(message);
  };
  /**
   * 机器人等待消息，带有异步操作
   * @param fn 异步操作
   * @returns
   */
  const robotWaitWithAsyncOperation = async <T>(fn: () => Promise<T> | T | void) => {
    addRobotWaitMessage();
    try {
      const result = await fn();
      return result;
    } catch (e) {
      failRobotWaitMessage();
    }
  };
  /**
   * 更新消息
   * @param id 消息ID
   * @param message 消息内容
   */
  const updateMessage = <T>(id: MessageId, message: Message<T>, notify: boolean = true, index: number = -1): Message<T> => {
    index = index >= 0 ? index : findIndex(id);
    if (index >= 0) {
      messages.value.splice(index, 1, { ...message });
      notify && subject.next(message);
    }
    return message;
  };
  /**
   * 更新机器人等待消息
   * @param message 机器人返回的消息
   */
  const updateRobotWaitMessage = <T>(message: RobotMessage<T>): Message<T> | null => {
    if (lastWaitResponseMessage.value === undefined) {
      return null;
    }
    const newMessage: Message<T> = {
      ...message,
      id: message.id || lastWaitResponseMessage.value.id,
      userId: message.userId || lastWaitResponseMessage.value.userId,
      avatar: message.avatar || lastWaitResponseMessage.value.avatar,
      status: message.status || 'success',
      content: message.content || (message.status === 'fail' ? t('library.RobotChat.thinkError') as any : message.content)
    };
    return updateMessage(lastWaitResponseMessage.value.id, newMessage);
  };

  /**
   * 设置机器人等待消息为失败
   * @returns
   */
  const failRobotWaitMessage = () => {
    if (!lastWaitResponseMessage.value) {
      return;
    }
    updateRobotWaitMessage({
      ...lastWaitResponseMessage.value,
      status: 'fail',
      content: undefined
    });
  };

  /**
   * 删除一条消息
   * @param id 消息ID
   */
  const deleteMessage = (id: MessageId) => {
    const index = findIndex(id);
    if (index > -1) {
      messages.value.splice(index, 1);
    }
  };

  /**
   * 插入消息
   * @param id 在哪个消息的位置插入
   * @param position start = 前面; after = 后面
   * @param message 插入的消息
   * @param notify 是否进行主题通知
   */
  const insertMessage = <T>(id: MessageId, position: 'start' | 'after', message: Message<T>, notify: boolean = false) => {
    const index = findIndex(id);
    if (index > -1) {
      messages.value.splice(position === 'start' ? index : index + 1, 0, message);
      notify && subject.next(message);
    }
  };

  /**
   * 获取下一条消息
   * @param id 消息ID
   * @returns
   */
  const nextMessage = (id: MessageId, filter?: (message: Message<any>) => boolean): Message<any> | null => {
    const index = findIndex(id);
    if (index > -1) {
      if (filter) {
        return messages.value.slice(index + 1).find(filter) || null;
      }
      return messages.value[index + 1] || null;
    }
    return null;
  };
  /**
   * 获取上一条消息
   * @param id
   * @param filter
   * @returns
   */
  const prevMessage = (id: MessageId, filter?: (message: Message<any>) => boolean): Message<any> | null => {
    const index = findIndex(id);
    if (index > -1) {
      if (filter) {
        return messages.value.slice(0, index).reverse().find(filter) || null;
      }
      return messages.value[index - 1] || null;
    }
    return null;
  };

  const findMessageForAddOrUpdate = (message: RobotMessage<any> | Message<any>): Message<any> | null => {
    const index = findLastIndex(message.id);
    if (index >= 0 && message.type !== 'ai.right.block') {
      return updateMessage(message.id, {
        ...message,
        status: message.status || 'success',
        userId: 0,
        avatar: botAvatar
      }, false, index);
    }

    const lastFailWaitResponseMessage = computed(() => messages.value.find(isFailResponseMessage));
    console.log(message, index, messages.value, lastWaitResponseMessage.value, isWaitResponseMessage, lastFailWaitResponseMessage.value);
    if (lastResponseMessage.value) {
    // if (message.type === 'ai.right.block') {
      //   deleteMessage(message.id);
      //   return null;
      // }
      return updateRobotWaitMessage(message);
    }

    return addMessage({
      ...message,
      userId: 0,
      avatar: botAvatar
    });
  };

  /**
   * 发送消息并修改id
   * @param updateMsg 要修改的消息
   * @param type
   * @param message
   * @param params
   * @returns
   */
  const sendMessageAndUpdateId = async <K extends SendMessageTypeName>(updateMsg: Message<any> | null, type: K, message: SendMessageType[K], info?: Info, params?: Record<string, any>): Promise<SendMessageReturnType<any>> => {
    // console.log('--sendMessageAndUpdateId--', type, message, info, params);
    const intent = info?.intent || params?.intent || message.actionJson?.example?.intent;
    const { getIntentInfo } = useIntentTypes();
    const intentInfo = getIntentInfo(intent); 
    const oldIntentInfo = getIntentInfo(oldIntent.value || ''); 
    if (info?.parabolaAnimation && intentInfo?.type !== oldIntentInfo?.type) {
      const target = info.event.target as HTMLElement;
      const targetDom = document.querySelector('.robot-chat-input');
      const targetDomClientRect = targetDom?.getBoundingClientRect();
      const sourcePos = {
        x: target.getBoundingClientRect()?.left,
        y: target.getBoundingClientRect()?.top
      };
      
      const movingNode = document.createElement('div');
      movingNode.classList.add('robot-chat-parabola-animation');
      movingNode.innerHTML = `<em class="zkicon ${intentInfo?.icon}"></em>${intentInfo?.text}`;
      const targetPos = {
        x: (targetDomClientRect?.left || 0) + 40,
        y: (targetDomClientRect?.top || 0) + 5
      };
      await parabolaAnimation(sourcePos, targetPos, movingNode);
      if (targetDom && targetDomClientRect) {
        // const textDom = targetDom.cloneNode(true) as HTMLElement;
        const container = document.querySelector('.robot-chat-wrapper');
        if (container) {
          const textDom = document.createElement('div');
          const clientRect = container.getBoundingClientRect();
          textDom.innerHTML = `/${intentInfo?.text} `;
          textDom.style.left = (targetDomClientRect.left + 12 - clientRect.x) + 'px';
          textDom.style.top = (targetDomClientRect.top + 8 - clientRect.y) + 'px';
          targetDom?.classList.add('robot-chat-hidden');
          container.appendChild(textDom);
          await textDebounceAnimation(textDom);
          container.removeChild(textDom);
        }
      }
      targetDom?.classList.remove('robot-chat-hidden');
    }
    
    instance.eventBus.$emit('robot-change-indent', { intent, info });
    instance.eventBus.$emit('finish-guide-task', 'MEET_MY_AI_ASSISTANT_RISK');
    instance.eventBus.$emit('finish-guide-task', 'MEET_MY_AI_ASSISTANT_REPORT');
    const result = await sendMessage<K>(type, message, params);
    if (result?.id && updateMsg?.id) {
      updateMessage(updateMsg.id, { ...updateMsg, id: result.id }, false);
    }
    return result;
  };

  return {
    loadMessage,
    loadNewMessage,
    addMessage,
    addOrUpdateMessage,
    addUserMessage,
    addUserTextMessage,
    addUserMessageWithPush,
    addRobotMessage,
    addRobotWaitMessage,
    robotWaitWithAsyncOperation,
    updateMessage,
    updateRobotWaitMessage,
    failRobotWaitMessage,
    deleteMessage,
    insertMessage,
    nextMessage,
    prevMessage,
    subscribe<T>(next: (value: Message<T> | Message<T>[]) => void) {
      const subscription = subject.asObservable().subscribe(next);
      onUnmounted(() => subscription.unsubscribe());
    },
    messages: shallowReadonly(messages),
    loading: shallowReadonly(loading),
    isEmpty: computed(() => messages.value.length <= 0),
    finished: shallowReadonly(finished),
    isWaitResponse,
    lastWaitResponseMessage,
    lastResponseMessage,
    findMessageForAddOrUpdate,
    sendMessageAndUpdateId
  };
};

export const useRecommendMessage = (message: Message<RecommendData>) => {
  const recommends = computed(() => {
    const content = message.content;
    return content.recommend?.map((item: any, index) => ({
      id: index,
      label: replaceRecommendLabel(item.content, item.params),
      item
    })) || [];
  });
  const isSingleRecommend = computed(() => recommends.value.length === 1);
  const singleTitle = computed(() => {
    const [ recommend ] = message.content.recommend || [];
    return isSingleRecommend.value ? replaceRecommendLabel(recommend.content, recommend.params) : '';
  });

  return {
    recommends,
    isSingleRecommend,
    singleTitle
  };
};
export const useSprintRiskRecommendMessage = (message:Message<SprintRecommendData>) => {
  const recommends = computed(() => {
    const content = message.content;
    return content.recommend?.map((item: any, index) => ({
      id: index,
      label: item.content,
      ...item
    })) || [];
  });
  const actionType = computed<actionType>(()=> message.content.actionType);
  const isCreateSprint = computed(() => actionType.value === 'sprint_risk_create_sprint');

  return {
    recommends,
    isCreateSprint,
    actionType
  };
};
